import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ListingGallery } from '@/components/listing/ListingGallery';
import { KeyDetails, Highlights, AreaSection, AgentContact, ListingFooter } from '@/components/listing/ListingSections';
import { Loader2, ArrowDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/lib/auth';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ListingPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const { isAuthenticated, loading: authLoading } = useAuth();

  useEffect(() => {
    if (authLoading) return;
    authFetch(`${API_BASE}/api/listings/${slug}/data`, {}, false)
      .then(async (r) => { if (!r.ok) throw new Error(); setData(await r.json()); })
      .catch(() => setError('This listing is not available.'));
  }, [slug, authLoading]);

  // The site-wide tags in index.html describe RC Studios services; swap in this listing's tags while the page is open.
  useEffect(() => {
    if (!data) return undefined;
    const nodes = [...document.head.querySelectorAll(
      'meta[name="description"], meta[name="keywords"], meta[property^="og:"], meta[name^="twitter:"], script[type="application/ld+json"]')];
    const placeholders = nodes.map((n) => { const c = document.createComment('listing'); n.replaceWith(c); return c; });
    const title = [data.street, data.city, data.state].filter(Boolean).join(', ');
    const desc = `Photos and details for ${title}. Photos by RC Studios.`;
    const image = `${API_BASE}/api/listings/${slug}/hero.jpg`;
    const prevTitle = document.title;
    document.title = title;
    const added = [['name', 'description', desc], ['property', 'og:type', 'website'], ['property', 'og:title', title],
      ['property', 'og:description', desc], ['property', 'og:image', image], ['property', 'og:url', window.location.href],
      ['name', 'twitter:card', 'summary_large_image'], ['name', 'twitter:title', title], ['name', 'twitter:image', image],
    ].map(([attr, key, content]) => {
      const m = document.createElement('meta');
      m.setAttribute(attr, key);
      m.setAttribute('content', content);
      document.head.appendChild(m);
      return m;
    });
    return () => { document.title = prevTitle; added.forEach((m) => m.remove()); placeholders.forEach((c, i) => c.replaceWith(nodes[i])); };
  }, [data, slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center text-center px-6" data-testid="listing-error">
        <p className="font-serif text-3xl text-white">Listing unavailable</p>
        <p className="text-[#E8E4DE]/60 mt-3">{error}</p>
        {!isAuthenticated && (
          <p className="text-[#E8E4DE]/60 mt-2 text-sm">
            Is this your listing? <Link to={`/login?returnTo=/listing/${slug}`} className="text-[#C6A14A] underline" data-testid="listing-signin-link">Sign in to preview it</Link>
          </p>
        )}
        <Link to="/" className="text-[#C6A14A] mt-6" data-testid="listing-home-link">Go to RC Studios</Link>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[#050816] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#C6A14A]" /></div>;

  const hero = data.photos.find((p) => p.id === data.hero_photo_id) || data.photos[0];
  const cityLine = [data.city, data.state].filter(Boolean).join(', ');
  const fullAddress = [data.street, cityLine, data.zip].filter(Boolean).join(', ');

  return (
    <div className="min-h-screen bg-[#050816]" data-testid="listing-page">
      {data.preview && (
        <div className="sticky top-0 z-[1000] bg-[#C6A14A] text-[#050816] text-center text-sm font-medium px-4 py-2" data-testid="listing-preview-banner">
          Preview: only you and RC Studios can see this. It goes public once the invoice is paid.
        </div>
      )}

      <header className="relative h-[88svh] min-h-[520px] w-full overflow-hidden" data-testid="listing-hero">
        {hero && <img src={hero.url} alt={fullAddress} className="absolute inset-0 h-full w-full object-cover animate-in fade-in duration-1000" data-testid="listing-hero-img" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050816] via-[#050816]/35 to-[#050816]/10" />
        <div className="absolute top-0 inset-x-0 px-4 sm:px-6 py-5 max-w-7xl mx-auto flex justify-between items-center">
          <span className="font-serif text-lg tracking-wide text-white/90">RC Studios</span>
        </div>
        <div className="absolute bottom-0 inset-x-0 max-w-7xl mx-auto px-4 sm:px-6 pb-14 sm:pb-20">
          <p className="text-xs uppercase tracking-[0.35em] text-[#C6A14A] animate-in fade-in slide-in-from-bottom-2 duration-700">Featured property</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mt-3 leading-tight animate-in fade-in slide-in-from-bottom-3 duration-700" data-testid="listing-address">{data.street}</h1>
          <p className="text-lg sm:text-xl text-[#E8E4DE]/80 mt-2 animate-in fade-in slide-in-from-bottom-4 duration-1000" data-testid="listing-city">{cityLine}</p>
          <a href="#gallery" className="inline-flex items-center gap-2 mt-8 h-11 px-5 rounded-full bg-[#C6A14A] text-[#050816] text-sm font-medium hover:bg-[#D4AF5A] transition-colors" data-testid="listing-view-photos-btn">
            View all {data.photos.length} photos <ArrowDown className="h-4 w-4" />
          </a>
        </div>
      </header>

      <KeyDetails data={data} />
      <Highlights items={data.highlights} />
      <div id="gallery"><ListingGallery photos={data.photos} address={fullAddress} /></div>
      <AreaSection fullAddress={fullAddress} city={data.city} />
      <AgentContact agent={data.agent} />
      <ListingFooter />
    </div>
  );
}
